import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { MdClose, MdUploadFile, MdPictureAsPdf, MdCheckCircle, MdWarning } from 'react-icons/md';

import Card from 'components/card';
import SearchSelector from 'components/fields/SearchSelector';
import { extractOrderDocument, ExtractedOrderItem } from 'interfaces/order';
import { searchProductsForSelect } from 'interfaces/products';

const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const AUTO_MATCH_THRESHOLD = 0.75;

interface Suggestion {
    productId: string;
    name: string;
    ref: string;
    unity: string;
    price: number;
    vat: number;
    brutWeight: number;
    netWeight: number;
    score: number;
}

interface ReviewRow {
    key: string;
    rawLabel: string;
    reference: string | null;
    unit: string | null;
    quantity: number;
    suggestions: Suggestion[];
    selectedProduct: Suggestion | null;
    included: boolean;
}

const ImportOrderDocumentModal = (props: {
    isOpen: boolean;
    onClose: () => void;
    addProducts: (items: any[]) => void;
    applyCustomerReference?: (ref: string) => void;
    customerId?: string;
}) => {
    const { isOpen, onClose, addProducts, applyCustomerReference, customerId } = props;

    const [step, setStep] = useState<'upload' | 'review'>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [customerReference, setCustomerReference] = useState<string | null>(null);
    const [rows, setRows] = useState<ReviewRow[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const reset = () => {
        setStep('upload');
        setFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setIsDragging(false);
        setIsAnalyzing(false);
        setCustomerReference(null);
        setRows([]);
    };

    useEffect(() => {
        if (isOpen) reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleFileSelected = (selected: File | undefined | null) => {
        if (!selected) return;
        if (!ACCEPTED_TYPES.includes(selected.type)) {
            toast.error('Formats acceptés : PDF, PNG, JPEG, WEBP.');
            return;
        }
        if (selected.size > 15 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 15 Mo).');
            return;
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(selected);
        setPreviewUrl(URL.createObjectURL(selected));
    };

    const analyze = async () => {
        if (!file) return;
        setIsAnalyzing(true);
        const res = await extractOrderDocument(file);
        setIsAnalyzing(false);

        if (res.error || !res.data) {
            toast.error(res.error || "L'analyse du document a échoué.");
            return;
        }

        if (res.data.items.length === 0) {
            toast.error('Aucune ligne de commande détectée dans ce document.');
            return;
        }

        setCustomerReference(res.data.customerReference);
        const newRows: ReviewRow[] = res.data.items.map((item: ExtractedOrderItem, idx: number) => {
            const suggestions = (item.suggestions || []) as Suggestion[];
            const best = suggestions[0];
            const autoMatch = best && best.score >= AUTO_MATCH_THRESHOLD ? best : null;
            return {
                key: `row-${idx}`,
                rawLabel: item.rawLabel,
                reference: item.reference,
                unit: item.unit,
                quantity: item.quantity ?? 1,
                suggestions,
                selectedProduct: autoMatch,
                included: true,
            };
        });
        setRows(newRows);
        setStep('review');
    };

    const updateRow = (key: string, patch: Partial<ReviewRow>) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    };

    const matchedCount = useMemo(
        () => rows.filter((r) => r.included && r.selectedProduct).length,
        [rows],
    );

    const handleConfirm = () => {
        const includedRows = rows.filter((r) => r.included);
        const missing = includedRows.filter((r) => !r.selectedProduct);
        if (missing.length > 0) {
            toast.error(
                `Veuillez sélectionner un produit pour "${missing[0].rawLabel}", ou décocher la ligne.`,
            );
            return;
        }
        if (includedRows.length === 0) {
            toast.error('Aucune ligne sélectionnée.');
            return;
        }

        const newItems = includedRows.map((r) => ({
            _id: r.selectedProduct!.productId,
            productId: r.selectedProduct!.productId,
            name: r.selectedProduct!.name,
            ref: r.selectedProduct!.ref,
            price: r.selectedProduct!.price,
            quantity: r.quantity,
            unity: r.selectedProduct!.unity,
            vat: r.selectedProduct!.vat,
            brutWeight: r.selectedProduct!.brutWeight,
            netWeight: r.selectedProduct!.netWeight,
            discountType: 'none',
            discountValue: 0,
        }));

        addProducts(newItems);
        if (customerReference && applyCustomerReference) {
            applyCustomerReference(customerReference);
        }
        toast.success(`${newItems.length} produit(s) ajouté(s) depuis le document.`);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="relative !z-50 mx-2 sm:mx-auto md:top-[7vh]">
                <ModalBody>
                    <Card
                        extra="mt-5 lg:mt-0 px-3 sm:px-[10px] lg:px-[30px] pt-[25px] sm:pt-[35px] pb-[30px] sm:pb-[40px] max-w-full sm:max-w-[95%] lg:max-w-[70%] w-full mx-auto flex flex-col"
                        style={{ maxHeight: '85vh' }}
                    >
                        <div className="absolute top-4 right-4">
                            <button
                                className="text-red-500 hover:text-red-600 hover:cursor-pointer"
                                onClick={onClose}
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                        <h1 className="text-base sm:text-lg font-bold">
                            Importer un bon de commande (PDF ou photo)
                        </h1>

                        {step === 'upload' && (
                            <>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600">
                                    L'IA extrait les références et quantités du document. Vous pourrez
                                    vérifier et corriger chaque ligne avant de l'ajouter à la commande.
                                </p>
                                <div
                                    className={`mt-5 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                                        isDragging
                                            ? 'border-toola-500 bg-toola-50'
                                            : 'border-gray-300 hover:border-toola-400'
                                    }`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setIsDragging(true);
                                    }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setIsDragging(false);
                                        handleFileSelected(e.dataTransfer.files?.[0]);
                                    }}
                                >
                                    <MdUploadFile size={32} className="text-toola-500" />
                                    <p className="text-sm text-gray-600">
                                        Glissez-déposez un fichier ici, ou cliquez pour parcourir
                                    </p>
                                    <p className="text-xs text-gray-400">PDF, PNG, JPEG, WEBP — 15 Mo max</p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={ACCEPTED_TYPES.join(',')}
                                        className="hidden"
                                        onChange={(e) => handleFileSelected(e.target.files?.[0])}
                                    />
                                </div>

                                {file && (
                                    <div className="mt-4 flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                                        {file.type === 'application/pdf' ? (
                                            <MdPictureAsPdf size={28} className="text-red-500 shrink-0" />
                                        ) : (
                                            <img
                                                src={previewUrl || ''}
                                                alt="Aperçu"
                                                className="h-14 w-14 rounded object-cover shrink-0"
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs sm:text-sm text-gray-700">{file.name}</p>
                                            {previewUrl && (
                                                <a
                                                    href={previewUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-toola-500 hover:underline"
                                                >
                                                    Ouvrir le document
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-5 flex gap-2 px-1">
                                    <button
                                        disabled={!file || isAnalyzing}
                                        className={`linear flex items-center justify-center rounded-xl px-6 py-3 sm:py-2 text-sm sm:text-xs font-medium text-white ${
                                            !file || isAnalyzing
                                                ? 'bg-[#32383D] cursor-default'
                                                : 'bg-[#0260CB] hover:bg-[#01499b] active:bg-[#024086]'
                                        }`}
                                        onClick={analyze}
                                    >
                                        {isAnalyzing ? 'Analyse en cours...' : 'Analyser le document'}
                                    </button>
                                </div>
                            </>
                        )}

                        {step === 'review' && (
                            <>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600">
                                    Vérifiez chaque ligne avant de l'ajouter : {matchedCount}/{rows.length} produit(s)
                                    reconnu(s) automatiquement.
                                </p>
                                {customerReference && (
                                    <p className="mt-2 text-xs sm:text-sm text-gray-600">
                                        Référence commande détectée : <span className="font-medium">{customerReference}</span>
                                    </p>
                                )}
                                <div className="mx-1 mt-3 grid grid-cols-1 gap-3 overflow-y-auto overflow-x-auto flex-1">
                                    <table className="w-full text-xs sm:text-sm text-left text-gray-500">
                                        <thead className="text-[10px] sm:text-xs text-gray-700 bg-gray-50">
                                            <tr>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Sur le document</th>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Produit correspondant</th>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Qté</th>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Ajouter</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row) => (
                                                <tr
                                                    key={row.key}
                                                    className="bg-white border-b text-gray-800 border-gray-200 align-top"
                                                >
                                                    <td className="px-1 sm:px-2 py-2">
                                                        <p className="text-xs sm:text-sm break-words">{row.rawLabel}</p>
                                                        <p className="text-[10px] sm:text-xs text-gray-400">
                                                            {row.reference && `Réf. ${row.reference}`}
                                                            {row.reference && row.unit && ' · '}
                                                            {row.unit}
                                                        </p>
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2 min-w-[220px]">
                                                        {row.selectedProduct ? (
                                                            <div className="flex items-center gap-2">
                                                                <MdCheckCircle className="text-green-500 shrink-0" size={16} />
                                                                <div className="min-w-0">
                                                                    <p className="truncate text-xs sm:text-sm">
                                                                        {row.selectedProduct.name}
                                                                        {row.selectedProduct.ref && ` (#${row.selectedProduct.ref})`}
                                                                    </p>
                                                                    <button
                                                                        className="text-[10px] sm:text-xs text-toola-500 hover:underline"
                                                                        onClick={() => updateRow(row.key, { selectedProduct: null })}
                                                                    >
                                                                        Changer
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <div className="flex items-center gap-1 mb-1">
                                                                    <MdWarning className="text-orange-500 shrink-0" size={14} />
                                                                    <span className="text-[10px] sm:text-xs text-orange-500">
                                                                        À vérifier
                                                                    </span>
                                                                </div>
                                                                {row.suggestions.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mb-1">
                                                                        {row.suggestions.slice(0, 3).map((s) => (
                                                                            <button
                                                                                key={s.productId}
                                                                                className="rounded-full border border-toola-300 px-2 py-0.5 text-[10px] sm:text-xs text-toola-600 hover:bg-toola-50"
                                                                                onClick={() => updateRow(row.key, { selectedProduct: s })}
                                                                                title={`Confiance ${Math.round(s.score * 100)}%`}
                                                                            >
                                                                                {s.name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <SearchSelector
                                                                    hideLabel
                                                                    inputSmall
                                                                    displayKey="label"
                                                                    options={[]}
                                                                    onSearch={async (term: string) => {
                                                                        const results = await searchProductsForSelect(term, customerId);
                                                                        return results.map((p: any) => ({
                                                                            ...p,
                                                                            productId: p._id,
                                                                            label: p.ref ? `${p.name} (#${p.ref})` : p.name,
                                                                        }));
                                                                    }}
                                                                    onSelect={(p: any) =>
                                                                        updateRow(row.key, {
                                                                            selectedProduct: {
                                                                                productId: p.productId,
                                                                                name: p.name,
                                                                                ref: p.ref,
                                                                                unity: p.unity,
                                                                                price: parseFloat(p.price),
                                                                                vat: parseFloat(p.vat),
                                                                                brutWeight: parseFloat(p.brutWeight) || 0,
                                                                                netWeight: parseFloat(p.netWeight) || 0,
                                                                                score: 1,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="w-[70px] h-[30px] px-2 text-xs sm:text-sm border border-gray-200 rounded-xl"
                                                            value={row.quantity}
                                                            onChange={(e) =>
                                                                updateRow(row.key, {
                                                                    quantity: parseFloat(e.target.value) || 0,
                                                                })
                                                            }
                                                        />
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={row.included}
                                                            onChange={(e) => updateRow(row.key, { included: e.target.checked })}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-5 flex gap-2 px-1">
                                    <button
                                        className="linear flex items-center justify-center rounded-xl border border-gray-300 px-4 py-3 sm:py-2 text-sm sm:text-xs font-medium text-gray-600 hover:bg-gray-50"
                                        onClick={() => setStep('upload')}
                                    >
                                        Retour
                                    </button>
                                    <button
                                        className="linear flex-1 sm:flex-none flex items-center justify-center rounded-xl bg-[#0260CB] px-4 sm:px-6 py-3 sm:py-2 text-sm sm:text-xs font-medium text-white hover:bg-[#01499b] active:bg-[#0260CB]"
                                        onClick={handleConfirm}
                                    >
                                        Ajouter à la commande
                                    </button>
                                </div>
                            </>
                        )}
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ImportOrderDocumentModal;
